import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { authUtils } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/api';
import ChatMenu from '../chat/ChatMenu';

interface GPSLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

interface Job {
  id: string;
  job_title: string;
  company: string;
  company_info?: string;
  description?: string;
  requirements?: string;
  benefits?: string;
  job_url?: string;
  location?: string;
  salary?: string;
}

interface SearchFilters {
  jobTitle: string;
  country: string;
  state: string;
  city: string;
}

const JobSearch: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { userName } = useAuth();
  const [searchLoading, setSearchLoading] = useState(false);
  const [gpsLocation, setGpsLocation] = useState<GPSLocation | null>(null);
  
  // Search filters
  const [filters, setFilters] = useState<SearchFilters>({
    jobTitle: '',
    country: '',
    state: '',
    city: ''
  });
  
  // Location data
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  
  // Job results
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  const jobListRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Check and get GPS location on mount
  useEffect(() => {
    checkGPSLocation();
    loadCountries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check GPS location from storage or request new one
  const checkGPSLocation = () => {
    const storedGPS = localStorage.getItem('gpsLocation');
    const deniedTimestamp = localStorage.getItem('gpsPermissionDenied');
    
    if (deniedTimestamp) {
      const deniedTime = parseInt(deniedTimestamp);
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (now - deniedTime < sevenDays) {
        // GPS permission was recently denied, don't ask again
        return;
      } else {
        localStorage.removeItem('gpsPermissionDenied');
      }
    }
    
    if (storedGPS) {
      const gps = JSON.parse(storedGPS) as GPSLocation;
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      
      if (now - gps.timestamp < sevenDays) {
        setGpsLocation(gps);
        return;
      }
    }
    
    // Request new GPS location
    requestGPSLocation();
  };

  const requestGPSLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const gps: GPSLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: Date.now()
          };
          setGpsLocation(gps);
          localStorage.setItem('gpsLocation', JSON.stringify(gps));
        },
        (error) => {
          console.error('GPS permission denied:', error);
          localStorage.setItem('gpsPermissionDenied', Date.now().toString());
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }
  };

  // Load countries on mount
  const loadCountries = async () => {
    try {
      const token = authUtils.getToken();
      const response = await axios.get(API_ENDPOINTS.JOB_SEARCH.COUNTRIES, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setCountries(response.data.countries || []);
      }
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  // Load states when country changes
  const loadStates = async (country: string) => {
    if (!country) {
      setStates([]);
      setCities([]);
      return;
    }
    
    try {
      const token = authUtils.getToken();
      const response = await axios.post(
        API_ENDPOINTS.JOB_SEARCH.STATES,
        { country },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const statesList = response.data.states || [];
        setStates(statesList);
        
        // If no states, load cities directly
        if (statesList.length === 0) {
          loadCities(country, '');
        } else {
          setCities([]);
        }
      }
    } catch (error) {
      console.error('Failed to load states:', error);
      setStates([]);
    }
  };

  // Load cities when state changes
  const loadCities = async (country: string, state: string) => {
    if (!country) {
      setCities([]);
      return;
    }
    
    try {
      const token = authUtils.getToken();
      const response = await axios.post(
        API_ENDPOINTS.JOB_SEARCH.CITIES,
        { country, state: state || undefined },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setCities(response.data.cities || []);
      }
    } catch (error) {
      console.error('Failed to load cities:', error);
      setCities([]);
    }
  };

  // Handle country change
  const handleCountryChange = (country: string) => {
    setFilters({
      ...filters,
      country,
      state: '',
      city: ''
    });
    loadStates(country);
  };

  // Handle state change
  const handleStateChange = (state: string) => {
    setFilters({
      ...filters,
      state,
      city: ''
    });
    if (filters.country) {
      loadCities(filters.country, state);
    }
  };

  // Handle city change
  const handleCityChange = (city: string) => {
    setFilters({ ...filters, city });
  };

  // Perform job search
  const performSearch = async (newOffset: number = 0) => {
    if (!filters.jobTitle.trim()) {
      return;
    }
    
    setSearchLoading(true);
    setSearchPerformed(true);
    
    try {
      const token = authUtils.getToken();
      const searchParams: any = {
        job_title: filters.jobTitle,
        limit: 10,
        offset: newOffset
      };
      
      // Add location parameters
      if (filters.country) searchParams.country = filters.country;
      if (filters.state) searchParams.state = filters.state;
      if (filters.city) searchParams.city = filters.city;
      
      // Add GPS if no specific location selected and GPS is available
      if (!filters.country && !filters.state && !filters.city && gpsLocation) {
        searchParams.latitude = gpsLocation.latitude;
        searchParams.longitude = gpsLocation.longitude;
      }
      
      const response = await axios.post(
        API_ENDPOINTS.JOB_SEARCH.GEO_SEARCH,
        searchParams,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const results = response.data.results || [];
        
        if (newOffset === 0) {
          setJobs(results);
        } else {
          setJobs(prev => [...prev, ...results]);
        }
        
        setOffset(newOffset);
        setHasMore(results.length === 10);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    setJobs([]);
    setOffset(0);
    setHasMore(true);
    performSearch(0);
  };

  // Handle Enter key in search box
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    const targetElement = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !searchLoading && searchPerformed) {
          performSearch(offset + 10);
        }
      },
      { 
        threshold: 1.0,
        rootMargin: '0px'
      }
    );

    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, hasMore, searchLoading, searchPerformed]);

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
  };

  const handleApply = (url?: string) => {
    if (url && selectedJob) {
      // Open the job application URL immediately for better UX
      window.open(url, '_blank');
      
      // Track the job application asynchronously in the background
      const trackJobApplication = async () => {
        try {
          const token = authUtils.getToken();
          
          await axios.post(
            `${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`,
            {
              job_title: selectedJob.job_title,
              job_url: url,
              company_name: selectedJob.company,
              status: 'Viewed',
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (error) {
          console.error('Failed to track job application:', error);
          // Silently fail - don't interrupt user experience
        }
      };
      
      // Execute tracking without blocking
      trackJobApplication();
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <ChatMenu />

      {/* Main content area */}
      <div className="flex-1 flex" style={{ height: 'calc(100vh - 0px)' }}>
        {/* Search and results panel */}
        <div className="flex-1 flex flex-col">
          {/* Search controls */}
          <div className="bg-white border-b p-4 pl-16 md:pl-4 flex-shrink-0">
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Job title search */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter job title (e.g., Software Engineer, Data Analyst)"
                  value={filters.jobTitle}
                  onChange={(e) => setFilters({ ...filters, jobTitle: e.target.value })}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSearch}
                  disabled={!filters.jobTitle.trim() || searchLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Location filters */}
              <div className="flex gap-2">
                <select
                  value={filters.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Country (Optional)</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>

                {states.length > 0 && (
                  <select
                    value={filters.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select State (Optional)</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                )}

                {cities.length > 0 && (
                  <select
                    value={filters.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select City (Optional)</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* GPS status indicator */}
              {gpsLocation && !filters.country && (
                <div className="text-sm text-green-600">
                  📍 Using your current location for better results
                </div>
              )}
            </div>
          </div>

          {/* Job listings */}
          <div className="flex-1 flex min-h-0">
            <div className="w-1/3 border-r flex flex-col">
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" ref={jobListRef}>
              {jobs.length === 0 && searchPerformed && !searchLoading && (
                <div className="p-8 text-center text-gray-500">
                  No jobs found. Try adjusting your search criteria.
                </div>
              )}
              
              {jobs.length === 0 && !searchPerformed && (
                <div className="p-8 text-center text-gray-500">
                  Enter a job title and click search to find opportunities
                </div>
              )}

              {jobs.map((job, index) => (
                <div
                  key={`${job.id}-${index}`}
                  onClick={() => handleJobClick(job)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                    selectedJob?.id === job.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                  }`}
                >
                  <h3 className="font-semibold text-gray-900">{job.job_title}</h3>
                  <p className="text-gray-600">{job.company}</p>
                  {job.location && (
                    <p className="text-sm text-gray-500 mt-1">📍 {job.location}</p>
                  )}
                  {job.salary && (
                    <p className="text-sm text-green-600 mt-1">💰 {job.salary}</p>
                  )}
                </div>
              ))}

              {searchLoading && (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading more jobs...</p>
                </div>
              )}

                {/* Infinite scroll trigger */}
                <div className="pb-4">
                  <div ref={observerTarget} className="h-1" />
                </div>
              </div>
            </div>

            {/* Job details panel */}
            <div className="flex-1 flex flex-col bg-white">
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {selectedJob ? (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{selectedJob.job_title}</h2>
                    <p className="text-xl text-gray-600 mt-2">{selectedJob.company}</p>
                    {selectedJob.location && (
                      <p className="text-gray-500 mt-1">📍 {selectedJob.location}</p>
                    )}
                    {selectedJob.salary && (
                      <p className="text-green-600 mt-1">💰 {selectedJob.salary}</p>
                    )}
                  </div>

                  {selectedJob.company_info && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-2">About the Company</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.company_info}</p>
                    </div>
                  )}

                  {selectedJob.description && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-2">Roles and Responsibilities</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.description}</p>
                    </div>
                  )}

                  {selectedJob.requirements && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-2">Qualifications</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.requirements}</p>
                    </div>
                  )}

                  {selectedJob.benefits && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-2">Benefits</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.benefits}</p>
                    </div>
                  )}

                  {selectedJob.job_url && (
                    <div className="mt-8">
                      <button
                        onClick={() => handleApply(selectedJob.job_url)}
                        className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                      >
                        Apply Now
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <p className="text-xl">Select a job to view details</p>
                    <p className="mt-2">Click on any job from the list to see more information</p>
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSearch;